const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

export interface DocumentCategory {
  value: string
  label: string
}

export interface OrderDocument {
  id: string
  name: string
  url: string
  source: 'upload' | 'chat'
  category?: string
  description?: string
  mime_type: string
  size: number
  uploaded_at: string
  uploaded_by: string
  uploaded_by_name?: string
}

export interface OrderDocumentWithOrder extends OrderDocument {
  order_id: string
  order_tx_control: string
}

export interface GetDocumentsResponse {
  documents: OrderDocumentWithOrder[]
  total: number
  limit: number
  offset: number
}

export interface GetOrderDocumentsResponse {
  documents: OrderDocument[]
}

export interface GetCategoriesResponse {
  categories: DocumentCategory[]
}

export interface UploadDocumentResponse {
  document: OrderDocument
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = localStorage.getItem('access_token')
  return {
    'Authorization': `Bearer ${token}`
  }
}

export const documentService = {
  /**
   * Get document categories
   */
  async getCategories(): Promise<GetCategoriesResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/documents/categories`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao buscar categorias')
    }

    return response.json()
  },

  /**
   * Get all documents for the current user (across all orders)
   */
  async getAllDocuments(limit = 50, offset = 0): Promise<GetDocumentsResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/documents?limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao buscar documentos')
    }

    return response.json()
  },

  /**
   * Get documents for a specific order
   */
  async getOrderDocuments(orderId: string): Promise<GetOrderDocumentsResponse> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/documents/orders/${orderId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao buscar documentos do pedido')
    }

    return response.json()
  },

  /**
   * Upload a document to an order
   */
  async uploadDocument(
    orderId: string,
    file: File,
    category?: string,
    description?: string
  ): Promise<UploadDocumentResponse> {
    const headers = await getAuthHeaders()

    const formData = new FormData()
    formData.append('file', file)
    if (category) {
      formData.append('category', category)
    }
    if (description) {
      formData.append('description', description)
    }

    const response = await fetch(
      `${API_URL}/documents/orders/${orderId}`,
      {
        method: 'POST',
        headers, // Don't set Content-Type for FormData
        body: formData
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao enviar documento')
    }

    return response.json()
  },

  /**
   * Delete a document from an order
   */
  async deleteDocument(orderId: string, documentId: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders()
    const response = await fetch(
      `${API_URL}/documents/orders/${orderId}/${documentId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro ao excluir documento')
    }

    return response.json()
  }
}

export default documentService
